// ---------------------------------------------------------------------------
// pricing-matrix.content.ts — Translated feature matrix for PricingPage
// ---------------------------------------------------------------------------
import type { SupportedLocale, LocaleContentMap } from '../i18n.config';

export interface PricingMatrixContent {
  categories: string[];
  features: string[][];
}

const en: PricingMatrixContent = {
  categories: ['Core PMS', 'Revenue & Analytics', 'Guest Management', 'F&B & POS', 'Compliance', 'Support', 'AI & Intelligence'],
  features: [
    ['Front Desk Dashboard', 'Room Status Board', 'Reservation Management', 'Walk-in Booking', 'Night Audit Wizard', 'Group Bookings'],
    ['Basic Occupancy Reports', 'RevPAR & ADR Tracking', 'Dynamic Pricing Rules', 'Competitor Rate Monitor', 'What-If Simulator', 'Demand Forecasting (AI)'],
    ['Guest Profiles', 'Online Check-in', 'Guest CRM & Preferences', 'Loyalty Program', 'Automated Communications'],
    ['Restaurant POS', 'Room Service Orders', 'Kitchen Display Board', 'Multi-Outlet Management'],
    ['Regional Tax Invoicing', 'Guest Registration Compliance', 'GDPR & Data Protection', 'Audit Trail'],
    ['Email Support', 'Priority Support', 'Dedicated Account Manager', '24/7 Phone Support', 'Custom SLA'],
    ['AI Dynamic Pricing Engine', 'AI Guest Concierge & Voice', 'Predictive Maintenance', 'AI Sentiment Analysis', 'Digital Twin & AI Analytics'],
  ],
};

const ar: PricingMatrixContent = {
  categories: ['نظام PMS الأساسي', 'الإيرادات والتحليلات', 'إدارة الضيوف', 'الأغذية والمشروبات ونقاط البيع', 'الامتثال', 'الدعم', 'الذكاء الاصطناعي'],
  features: [
    ['لوحة مكتب الاستقبال', 'لوحة حالة الغرف', 'إدارة الحجوزات', 'حجز بدون موعد', 'معالج التدقيق الليلي', 'الحجوزات الجماعية'],
    ['تقارير الإشغال الأساسية', 'تتبع RevPAR و ADR', 'قواعد التسعير الديناميكي', 'مراقبة أسعار المنافسين', 'محاكي ماذا لو', 'التنبؤ بالطلب (AI)'],
    ['ملفات الضيوف', 'تسجيل الوصول عبر الإنترنت', 'CRM الضيوف والتفضيلات', 'برنامج الولاء', 'الاتصالات الآلية'],
    ['نقطة بيع المطعم', 'طلبات خدمة الغرف', 'لوحة عرض المطبخ', 'إدارة المنافذ المتعددة'],
    ['فواتير الضرائب الإقليمية', 'امتثال تسجيل الضيوف', 'GDPR وحماية البيانات', 'سجل التدقيق'],
    ['دعم البريد الإلكتروني', 'دعم ذو أولوية', 'مدير حساب مخصص', 'دعم هاتفي ٢٤/٧', 'SLA مخصص'],
    ['محرك التسعير الديناميكي AI', 'مساعد ضيوف AI والصوت', 'الصيانة التنبؤية', 'تحليل المشاعر AI', 'التوأم الرقمي وتحليلات AI'],
  ],
};

const th: PricingMatrixContent = {
  categories: ['PMS หลัก', 'รายได้และวิเคราะห์', 'การจัดการแขก', 'อาหารและเครื่องดื่ม & POS', 'การปฏิบัติตามกฎระเบียบ', 'การสนับสนุน', 'AI และปัญญาอัจฉริยะ'],
  features: [
    ['แดชบอร์ดฟร้อนท์เดสก์', 'บอร์ดสถานะห้อง', 'การจัดการการจอง', 'การจองแบบ Walk-in', 'ตัวช่วย Night Audit', 'การจองกลุ่ม'],
    ['รายงานอัตราการเข้าพักพื้นฐาน', 'การติดตาม RevPAR และ ADR', 'กฎการตั้งราคาแบบไดนามิก', 'การตรวจสอบราคาคู่แข่ง', 'ตัวจำลอง What-If', 'การพยากรณ์ความต้องการ (AI)'],
    ['โปรไฟล์แขก', 'เช็คอินออนไลน์', 'CRM แขกและความชอบ', 'โปรแกรมสมาชิก', 'การสื่อสารอัตโนมัติ'],
    ['POS ร้านอาหาร', 'คำสั่งรูมเซอร์วิส', 'จอแสดงผลครัว', 'การจัดการหลายจุดขาย'],
    ['ใบแจ้งภาษีภูมิภาค', 'การปฏิบัติตามการลงทะเบียนแขก', 'GDPR และการปกป้องข้อมูล', 'เส้นทางการตรวจสอบ'],
    ['สนับสนุนอีเมล', 'สนับสนุนลำดับความสำคัญ', 'ผู้จัดการบัญชีเฉพาะ', 'สนับสนุนโทรศัพท์ 24/7', 'SLA แบบกำหนดเอง'],
    ['AI กำหนดราคาแบบไดนามิก', 'AI Concierge และเสียง', 'การบำรุงรักษาเชิงคาดการณ์', 'การวิเคราะห์ความรู้สึก AI', 'Digital Twin และ AI Analytics'],
  ],
};

const es: PricingMatrixContent = {
  categories: ['PMS Principal', 'Ingresos y Análisis', 'Gestión de Huéspedes', 'F&B y POS', 'Cumplimiento', 'Soporte', 'IA e Inteligencia'],
  features: [
    ['Panel de Recepción', 'Tablero de Estado de Habitaciones', 'Gestión de Reservas', 'Reserva sin Cita', 'Asistente de Auditoría Nocturna', 'Reservas Grupales'],
    ['Informes Básicos de Ocupación', 'Seguimiento RevPAR y ADR', 'Reglas de Precios Dinámicos', 'Monitor de Tarifas Competidoras', 'Simulador What-If', 'Pronóstico de Demanda (IA)'],
    ['Perfiles de Huéspedes', 'Check-in en Línea', 'CRM de Huéspedes y Preferencias', 'Programa de Fidelidad', 'Comunicaciones Automatizadas'],
    ['POS de Restaurante', 'Pedidos de Servicio a Habitación', 'Tablero de Cocina', 'Gestión Multi-Punto de Venta'],
    ['Facturación de Impuestos Regionales', 'Cumplimiento de Registro de Huéspedes', 'GDPR y Protección de Datos', 'Registro de Auditoría'],
    ['Soporte por Email', 'Soporte Prioritario', 'Gerente de Cuenta Dedicado', 'Soporte Telefónico 24/7', 'SLA Personalizado'],
    ['Motor de Precios Dinámicos IA', 'Concierge IA y Voz', 'Mantenimiento Predictivo', 'Análisis de Sentimiento IA', 'Gemelo Digital y Analítica IA'],
  ],
};

const fr: PricingMatrixContent = {
  categories: ['PMS Principal', 'Revenus et Analytique', 'Gestion des Clients', 'F&B et POS', 'Conformité', 'Support', 'IA et Intelligence'],
  features: [
    ['Tableau de Bord Réception', 'Tableau de Statut des Chambres', 'Gestion des Réservations', 'Réservation sans Rendez-vous', 'Assistant d\'Audit Nocturne', 'Réservations de Groupes'],
    ['Rapports d\'Occupation de Base', 'Suivi RevPAR et ADR', 'Règles de Tarification Dynamique', 'Surveillance des Tarifs Concurrents', 'Simulateur What-If', 'Prévision de la Demande (IA)'],
    ['Profils des Clients', 'Check-in en Ligne', 'CRM Clients et Préférences', 'Programme de Fidélité', 'Communications Automatisées'],
    ['POS Restaurant', 'Commandes Room Service', 'Écran de Cuisine', 'Gestion Multi-Points de Vente'],
    ['Facturation Fiscale Régionale', 'Conformité d\'Enregistrement des Clients', 'RGPD et Protection des Données', 'Piste d\'Audit'],
    ['Support Email', 'Support Prioritaire', 'Gestionnaire de Compte Dédié', 'Support Téléphonique 24/7', 'SLA Personnalisé'],
    ['Moteur de Tarification Dynamique IA', 'Concierge IA et Vocal', 'Maintenance Prédictive', 'Analyse de Sentiment IA', 'Jumeau Numérique et Analytique IA'],
  ],
};

const de: PricingMatrixContent = {
  categories: ['Kern-PMS', 'Umsatz und Analytik', 'Gästemanagement', 'F&B und POS', 'Compliance', 'Support', 'KI und Intelligenz'],
  features: [
    ['Rezeption-Dashboard', 'Zimmerstatus-Board', 'Reservierungsverwaltung', 'Walk-in-Buchung', 'Nachtaudit-Assistent', 'Gruppenbuchungen'],
    ['Basis-Auslastungsberichte', 'RevPAR- und ADR-Tracking', 'Dynamische Preisregeln', 'Wettbewerber-Ratenmonitor', 'Was-Wäre-Wenn-Simulator', 'Nachfrageprognose (KI)'],
    ['Gästeprofile', 'Online-Check-in', 'Gäste-CRM und Präferenzen', 'Treueprogramm', 'Automatisierte Kommunikation'],
    ['Restaurant-POS', 'Zimmerservice-Bestellungen', 'Küchenanzeige', 'Multi-Outlet-Management'],
    ['Regionale Steuerrechnungen', 'Gästeregistrierung-Compliance', 'DSGVO und Datenschutz', 'Audit-Trail'],
    ['E-Mail-Support', 'Prioritäts-Support', 'Dedizierter Account-Manager', '24/7 Telefon-Support', 'Individuelles SLA'],
    ['KI Dynamische Preisgestaltung', 'KI Concierge und Sprache', 'Prädiktive Wartung', 'KI Sentiment-Analyse', 'Digital Twin und KI-Analytik'],
  ],
};

const ja: PricingMatrixContent = {
  categories: ['コアPMS', '収益と分析', 'ゲスト管理', 'F&B・POS', 'コンプライアンス', 'サポート', 'AI・インテリジェンス'],
  features: [
    ['フロントデスクダッシュボード', '客室ステータスボード', '予約管理', 'ウォークイン予約', 'ナイトオーディットウィザード', 'グループ予約'],
    ['基本稼働率レポート', 'RevPAR・ADRトラッキング', 'ダイナミックプライシングルール', '競合料金モニター', 'What-Ifシミュレーター', '需要予測（AI）'],
    ['ゲストプロファイル', 'オンラインチェックイン', 'ゲストCRM・嗜好管理', 'ロイヤルティプログラム', '自動コミュニケーション'],
    ['レストランPOS', 'ルームサービス注文', 'キッチンディスプレイ', 'マルチアウトレット管理'],
    ['地域税請求書', 'ゲスト登録コンプライアンス', 'GDPR・データ保護', '監査証跡'],
    ['メールサポート', '優先サポート', '専任アカウントマネージャー', '24時間電話サポート', 'カスタムSLA'],
    ['AIダイナミックプライシング', 'AIコンシェルジュ・音声', '予測保全', 'AI感情分析', 'デジタルツイン・AI分析'],
  ],
};

const zh: PricingMatrixContent = {
  categories: ['核心PMS', '收入与分析', '客人管理', '餐饮与POS', '合规', '支持', 'AI与智能'],
  features: [
    ['前台仪表板', '房态看板', '预订管理', '散客入住', '夜审向导', '团队预订'],
    ['基础入住率报表', 'RevPAR和ADR追踪', '动态定价规则', '竞争对手费率监控', 'What-If模拟器', '需求预测（AI）'],
    ['客人档案', '在线入住登记', '客户CRM与偏好', '忠诚度计划', '自动化沟通'],
    ['餐厅POS', '客房服务订单', '厨房显示屏', '多门店管理'],
    ['地区税务发票', '客人登记合规', 'GDPR与数据保护', '审计追踪'],
    ['邮件支持', '优先支持', '专属客户经理', '24/7电话支持', '定制SLA'],
    ['AI动态定价引擎', 'AI礼宾和语音', '预测性维护', 'AI情感分析', '数字孪生与AI分析'],
  ],
};

const hi: PricingMatrixContent = {
  categories: ['कोर PMS', 'राजस्व और एनालिटिक्स', 'अतिथि प्रबंधन', 'F&B और POS', 'अनुपालन', 'सहायता', 'AI और बुद्धिमत्ता'],
  features: [
    ['फ्रंट डेस्क डैशबोर्ड', 'रूम स्टेटस बोर्ड', 'आरक्षण प्रबंधन', 'वॉक-इन बुकिंग', 'नाइट ऑडिट विज़ार्ड', 'ग्रुप बुकिंग'],
    ['बेसिक ऑक्युपेंसी रिपोर्ट', 'RevPAR और ADR ट्रैकिंग', 'डायनामिक प्राइसिंग रूल्स', 'कॉम्पीटिटर रेट मॉनिटर', 'वॉट-इफ सिम्युलेटर', 'डिमांड फॉरकास्टिंग (AI)'],
    ['अतिथि प्रोफाइल', 'ऑनलाइन चेक-इन', 'अतिथि CRM और प्रेफरेंसेज', 'लॉयल्टी प्रोग्राम', 'ऑटोमेटिड कम्युनिकेशन'],
    ['रेस्टॉरंट POS', 'रूम सर्विस ऑर्डर', 'किचन डिस्प्ले बोर्ड', 'मल्टी-आउटलेट मैनेजमेंट'],
    ['रीजनल टैक्स इनवॉइसिंग', 'अतिथि रजिस्ट्रेशन कॉम्प्लायंस', 'GDPR और डेटा प्रोटेक्शन', 'ऑडिट ट्रेल'],
    ['ईमेल सहायता', 'प्रायॉरिटी सहायता', 'डेडिकेटेड अकाउंट मैनेजर', '24/7 फोन सहायता', 'कस्टम SLA'],
    ['AI डायनामिक प्राइसिंग', 'AI कंसीयज और वॉइस', 'प्रेडिक्टिव मेंटेनेंस', 'AI सेंटिमेंट एनालिसिस', 'डिजिटल ट्विन और AI एनालिटिक्स'],
  ],
};

export const PRICING_MATRIX_CONTENT: LocaleContentMap<PricingMatrixContent> = { en, ar, th, es, fr, de, ja, zh, hi };
